import React, { useMemo } from "react";
import {
  FiCopy,
  FiDownload,
  FiExternalLink,
  FiFile,
  FiFileText,
  FiFilm,
  FiImage,
  FiMusic,
  FiPaperclip
} from "react-icons/fi";
import Modal from "./Modal/Modal";
import "./FilePreviewModal.css";

const imageExts = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif"]);
const pdfExts = new Set(["pdf"]);
const videoExts = new Set(["mp4", "webm", "ogg", "mov", "m4v"]);
const audioExts = new Set(["mp3", "wav", "ogg", "m4a", "aac", "flac"]);
const textExts = new Set([
  "txt",
  "log",
  "json",
  "csv",
  "md",
  "xml",
  "html",
  "htm",
  "css",
  "js",
  "jsx",
  "ts",
  "tsx",
  "sql",
  "yml",
  "yaml"
]);

const cleanUrl = (value = "") => String(value || "").trim();

const getFirebaseObjectPath = (value = "") => {
  try {
    const url = new URL(value);
    if (url.host !== "firebasestorage.googleapis.com") return "";
    const idx = url.pathname.indexOf("/o/");
    if (idx === -1) return "";
    return decodeURIComponent(url.pathname.slice(idx + 3) || "");
  } catch {
    return "";
  }
};

const getUrlPath = (value = "") => {
  const firebasePath = getFirebaseObjectPath(value);
  if (firebasePath) return firebasePath;

  try {
    return decodeURIComponent(new URL(value).pathname || "");
  } catch {
    return decodeURIComponent(String(value || "").split("?")[0].split("#")[0]);
  }
};

export const getPreviewFileName = (value = "", fallback = "Dosya") => {
  const path = getUrlPath(value);
  const name = path.split("/").filter(Boolean).pop();
  return name || fallback || "Dosya";
};

export const getPreviewExtension = (value = "") => {
  const name = getPreviewFileName(value, "");
  const cleanName = String(name || "").split("?")[0].split("#")[0];
  const parts = cleanName.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

export const getPreviewSourceLabel = (value = "") => {
  const firebasePath = getFirebaseObjectPath(value);
  if (firebasePath) return firebasePath;

  try {
    return new URL(value).host;
  } catch {
    return cleanUrl(value);
  }
};

export const detectFilePreviewKind = (value = "", forcedKind = "") => {
  const forced = String(forcedKind || "").toLowerCase();
  if (forced === "external") return "web";
  if (["image", "pdf", "video", "audio", "text", "web", "file"].includes(forced)) {
    return forced;
  }

  const ext = getPreviewExtension(value);
  if (imageExts.has(ext)) return "image";
  if (pdfExts.has(ext)) return "pdf";
  if (videoExts.has(ext)) return "video";
  if (audioExts.has(ext)) return "audio";
  if (textExts.has(ext)) return "text";

  const url = cleanUrl(value);
  if (/^https?:\/\//i.test(url) && !ext) return "web";

  return "file";
};

const iconForKind = (kind) => {
  if (kind === "image") return <FiImage />;
  if (kind === "video") return <FiFilm />;
  if (kind === "audio") return <FiMusic />;
  if (kind === "text" || kind === "pdf") return <FiFileText />;
  return <FiPaperclip />;
};

const copyToClipboard = async (text) => {
  const value = cleanUrl(text);
  if (!value) return;

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const ta = document.createElement("textarea");
  ta.value = value;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
};

const downloadUrl = (url, name) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = name || getPreviewFileName(url);
  a.target = "_blank";
  a.rel = "noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const openInNewTab = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const FilePreviewModal = ({
  open,
  close,
  url,
  title,
  kind,
  onCopy,
  onDownload
}) => {
  const clean = cleanUrl(url);
  const previewKind = useMemo(() => detectFilePreviewKind(clean, kind), [clean, kind]);
  const fileName = useMemo(
    () => title || getPreviewFileName(clean, "Dosya"),
    [clean, title]
  );
  const sourceLabel = useMemo(() => getPreviewSourceLabel(clean), [clean]);

  const handleCopy = async () => {
    if (!clean) return;
    if (typeof onCopy === "function") {
      await onCopy(clean);
      return;
    }
    await copyToClipboard(clean);
  };

  const handleDownload = async () => {
    if (!clean) return;
    if (typeof onDownload === "function") {
      await onDownload(clean, fileName);
      return;
    }
    downloadUrl(clean, fileName);
  };

  const preview = (() => {
    if (!clean) return null;

    if (previewKind === "image") {
      return (
        <div className="file-preview-stage image">
          <img src={clean} alt={fileName} />
        </div>
      );
    }

    if (previewKind === "pdf" || previewKind === "text" || previewKind === "web") {
      return (
        <div className={`file-preview-stage ${previewKind}`}>
          <iframe className="file-preview-frame" title={fileName} src={clean} />
          {previewKind === "web" ? (
            <p className="file-preview-note">
              Sayfa önizlemeyi engelliyorsa yeni sekmede açabilirsiniz.
            </p>
          ) : null}
        </div>
      );
    }

    if (previewKind === "video") {
      return (
        <div className="file-preview-stage video">
          <video controls src={clean}>
            Tarayıcınız video önizlemeyi desteklemiyor.
          </video>
        </div>
      );
    }

    if (previewKind === "audio") {
      return (
        <div className="file-preview-stage audio">
          <FiMusic size={42} />
          <audio controls src={clean}>
            Tarayıcınız ses önizlemeyi desteklemiyor.
          </audio>
        </div>
      );
    }

    return (
      <div className="file-preview-stage empty">
        <FiFile size={44} />
        <strong>Bu dosya türü tarayıcı içinde önizlenemiyor</strong>
        <p>Dosyayı yeni sekmede açabilir veya indirebilirsiniz.</p>
      </div>
    );
  })();

  return (
    <Modal open={open} close={close} cardClassName="file-preview-card">
      <div className="file-preview-shell">
        <div className="file-preview-head">
          <div className="file-preview-title-wrap">
            <span className={`file-preview-icon ${previewKind}`}>{iconForKind(previewKind)}</span>
            <div>
              <h2>{fileName}</h2>
              {sourceLabel ? <p>{sourceLabel}</p> : null}
            </div>
          </div>

          <div className="file-preview-actions">
            <button type="button" onClick={handleCopy}>
              <FiCopy size={15} />
              <span>Kopyala</span>
            </button>
            <button type="button" onClick={() => openInNewTab(clean)}>
              <FiExternalLink size={15} />
              <span>Aç</span>
            </button>
            <button type="button" className="primary" onClick={handleDownload}>
              <FiDownload size={15} />
              <span>İndir</span>
            </button>
          </div>
        </div>

        {preview}
      </div>
    </Modal>
  );
};

export default FilePreviewModal;
